/**
 * The render tier both live scenes share (the collection flacon and the hero
 * set), decided once per page.
 *
 *  - `studio`: the cap and the bottle wall cut from one refractive,
 *    dispersive stone ray-marched per pixel, metallic gold, MSAA, DPR up to
 *    2, and the studio light rig. One scene pass per frame.
 *  - `lite`: the previous family — tinted flat-shaded dielectrics, no
 *    transmission, no MSAA, DPR 1.4 (1.75 on phones) — one scene pass per frame.
 *
 * Studio is granted only when every signal says the machine can afford it:
 * WebGL2, a hardware rasteriser that is not a known weak mobile or integrated
 * family, a viewport at least 760px wide, no Save-Data, and at least four
 * logical cores. `?tier=studio` or `?tier=lite` overrides the decision for
 * verification. `decideRenderTier` is pure so the heuristic unit-tests with a
 * fake probe; `renderTier` runs the probe, is SSR-safe, and memoises so the two
 * scenes on the homepage share one decision and one throw-away context.
 */

export type RenderTier = 'studio' | 'lite'

export interface TierSignals {
  /** A WebGL2 context could be created. */
  readonly webgl2: boolean
  /** WEBGL_debug_renderer_info's unmasked renderer, or '' when unavailable. */
  readonly renderer: string
  readonly viewportWidth: number
  readonly saveData: boolean
  readonly hardwareConcurrency: number
  /** The `tier` query parameter, if any. */
  readonly override?: string | null | undefined
}

export const STUDIO_MIN_VIEWPORT = 760
export const STUDIO_MIN_CORES = 4

/** CPU rasterisers: headless CI, VMs, machines that fell back from a blocklisted driver. */
export const SOFTWARE_RENDERER = /swiftshader|llvmpipe|softpipe|software rasterizer|microsoft basic render/i

/**
 * GPU families known to struggle with the studio tier (the list was drawn up
 * when it ran a full-resolution transmission pass plus MSAA): older Mali and Adreno mobile parts, PowerVR, the Broadcom
 * VideoCore, and Intel's HD/UHD integrated line (Iris Xe and Arc are not
 * matched). The list is deliberately conservative — an unknown renderer string
 * gets studio, because the viewport and core-count gates still apply.
 */
export const WEAK_GPU =
  /\bmali-(4|t)\d*|\bmali-g(3|5)\d\b|adreno( \(tm\))? [1-5]\d\d\b|powervr|videocore|intel\(r\)? ?(hd|uhd) graphics|intel (hd|uhd) graphics/i

export function decideRenderTier(signals: TierSignals): RenderTier {
  const override = signals.override?.trim().toLowerCase()
  if (override === 'studio' || override === 'lite') return override
  if (!signals.webgl2) return 'lite'
  if (SOFTWARE_RENDERER.test(signals.renderer)) return 'lite'
  if (WEAK_GPU.test(signals.renderer)) return 'lite'
  if (signals.viewportWidth < STUDIO_MIN_VIEWPORT) return 'lite'
  if (signals.saveData) return 'lite'
  if (signals.hardwareConcurrency < STUDIO_MIN_CORES) return 'lite'
  return 'studio'
}

/**
 * Reads the signals off the live browser. The WebGL2 probe must not keep a
 * context alive — both stages run a canvas of their own and browsers cap live
 * contexts per document — so it is lost again as soon as it has been read.
 */
export function probeTierSignals(): TierSignals {
  let webgl2 = false
  let renderer = ''
  try {
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('webgl2')
    if (context) {
      webgl2 = true
      const info = context.getExtension('WEBGL_debug_renderer_info')
      renderer = info === null ? '' : String(context.getParameter(info.UNMASKED_RENDERER_WEBGL) ?? '')
      context.getExtension('WEBGL_lose_context')?.loseContext()
    }
  } catch {
    webgl2 = false
  }
  const nav = navigator as Navigator & { connection?: { saveData?: boolean } }
  let override: string | null = null
  try {
    override = new URLSearchParams(window.location.search).get('tier')
  } catch {
    override = null
  }
  return {
    webgl2,
    renderer,
    viewportWidth: window.innerWidth,
    saveData: Boolean(nav.connection?.saveData),
    hardwareConcurrency: typeof nav.hardwareConcurrency === 'number' ? nav.hardwareConcurrency : 0,
    override,
  }
}

let decided: RenderTier | null = null

/** The page's tier, decided on first call in the browser; always `lite` on the server. */
export function renderTier(): RenderTier {
  if (typeof window === 'undefined' || typeof document === 'undefined') return 'lite'
  if (decided === null) decided = decideRenderTier(probeTierSignals())
  return decided
}

/** Test seam: forget the memoised decision. */
export function resetRenderTier(): void {
  decided = null
}

/**
 * The device pixel ratio cap below which phones live; the previous card
 * (`dpr={[1, 1.75]}`) drew at this on every viewport, so it is what a 2x/3x
 * phone must keep — at 1 the lite label type and cap edges visibly soften.
 * A phone canvas is small (about 300 CSS px), so 1.75 there costs fewer
 * pixels than 1.4 does on a 520 px lite desktop card.
 */
export const PHONE_PIXEL_RATIO_CAP = 1.75

/**
 * The device pixel ratio cap the tier draws at: 2 on desktop and 1.5 on
 * tablets for studio, 1.4 for lite, and 1.75 on phones (< 760px) in either
 * tier — phones always decide lite unless `?tier=studio` overrides.
 */
export function tierPixelRatioCap(tier: RenderTier, viewportWidth: number): number {
  if (viewportWidth < STUDIO_MIN_VIEWPORT) return PHONE_PIXEL_RATIO_CAP
  if (tier === 'lite') return 1.4
  return viewportWidth < 1100 ? 1.5 : 2
}
